import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { api } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';
import type { User } from '@echo-work-platform/shared-types';

// Users are created/refreshed via JIT-sync on every EntraID login (see
// plugins/auth.ts) — the admin UI just lists them. There is no manual
// import flow.
export function Users(): JSX.Element {
  const users = useQuery({
    queryKey: ['/api/admin/users'],
    queryFn: () => api.get<User[]>('/api/admin/users'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Users</h2>
        <button className="btn-secondary" onClick={() => void users.refetch()}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left">Név</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Job Title</th>
              <th className="px-4 py-2 text-left">Department</th>
              <th className="px-4 py-2 text-left">Aktív</th>
              <th className="px-4 py-2 text-left">Utolsó login</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {users.data?.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-2">{u.name}</td>
                <td className="px-4 py-2 font-mono text-xs text-neutral-600">{u.email}</td>
                <td className="px-4 py-2">{u.jobTitle ?? '—'}</td>
                <td className="px-4 py-2">{u.department ?? '—'}</td>
                <td className="px-4 py-2">{u.isActive ? '✅' : '🚫'}</td>
                <td className="px-4 py-2">{formatDate(u.lastLoginAt)}</td>
                <td className="px-4 py-2">
                  <Link className="text-accent-600 hover:underline" to={`/users/${u.id}`}>
                    Részletek
                  </Link>
                </td>
              </tr>
            ))}
            {users.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-neutral-500">
                  Még senki nem jelentkezett be — az első sikeres EntraID-login után jelenik meg itt.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
